import { MINIMUM_CHECKOUT_BOOK_QUANTITY } from 'modules/v2/pages/constants';

type CustomerPlanInfo = {
  // Legacy Chargify component fields. They are never populated any more and no
  // consumer reads them; they stay optional so the hook's return type is unchanged.
  id?: number;
  name?: string;
  kind?: string;
  unitName?: string;
  currency?: string;
  allocatedQuantity?: number;
  pricingScheme?: string;
  componentId?: number;
  componentHandle?: string;
  allowFractionalQuantities?: boolean;
  subscriptionId?: number;
  recurring?: boolean;
  upgradeCharge?: number | null;
  downgradeCredit?: number | null;
  description?: string;
  archivedAt?: string | null;
  pricePointId?: number;
  pricePointHandle?: string;
  pricePointType?: string;
  pricePointName?: string;
  useSiteExchangeRate?: boolean;
  productFamilyId?: number;
  productFamilyHandle?: string;
  displayOnHostedPage?: boolean;
  createdAt?: string;
  updatedAt?: string;
  unitPrice?: string;
  isDigital: boolean;
  isTrialing: boolean;
  canOrderMoreThanTenBooks: boolean;
  trialCreditsInfo: {
    isAllowedBuyMoreThanOnce: boolean;
    bookCredits: number;
  }
};

export const handleAllowMoreBooksTrial = (
  creditsHistory: {
        value: string,
        versions: [],
    },
  isTrialing: boolean
) => {
  const creditsValue = Number(creditsHistory?.value);
  // Verify if the user has at least 2 times the minimum quantity of books to be able to buy more than once
  const hasMinimumCredits = creditsValue / MINIMUM_CHECKOUT_BOOK_QUANTITY >= 2;
  const hasMultipleVersions = creditsHistory?.versions?.length > 1;

  const isAllowedBuyMoreThanOnce = isTrialing && hasMultipleVersions && hasMinimumCredits;
  const bookCredits = isAllowedBuyMoreThanOnce ? MINIMUM_CHECKOUT_BOOK_QUANTITY : creditsValue;

  return { isAllowedBuyMoreThanOnce, bookCredits };
};

// The plan gates were derived from the legacy Chargify subscription
// (GET /payment-chargify/subscriptions). The Chargify account is cancelled, so
// that call answered 500 for every member and every gate evaluated to false.
// The call is removed and the gates keep that effective value: the hook makes no
// network request. Re-deriving them from Chargebee would newly restrict members
// and is a separate product decision (hubspot-off-block-3 task 95).
const INERT_PLAN_INFO: CustomerPlanInfo = Object.freeze({
  isDigital: false,
  isTrialing: false,
  canOrderMoreThanTenBooks: false,
  trialCreditsInfo: Object.freeze({
    isAllowedBuyMoreThanOnce: false,
    bookCredits: 0,
  }),
});

export const useCustomerPlanInfo = (): CustomerPlanInfo => INERT_PLAN_INFO;
